/**
 * 8 Ways to Think With Claude
 *
 * The guide document is unchanged; only its wrapper is. It used to carry
 * its own 49px strip with a back link and no footer, so a visitor who landed
 * here from an email had no wordmark, no account control and no way into the
 * rest of the site. GuideShell supplies all three, and GuideFrame lets the
 * document size itself so the footer sits after it rather than behind a
 * second scrollbar.
 */

import GuideShell from "@/components/GuideShell";
import GuideFrame from "@/components/GuideFrame";

const HTML_SRC = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>8 Ways to Think With Claude | Caren Glasser</title>
<link rel="preconnect" href="https://fonts.googleapis.com">
<link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
<link href="https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,400;0,9..144,500;0,9..144,600;0,9..144,700;1,9..144,400;1,9..144,600&family=Plus+Jakarta+Sans:wght@400;500;600;700&display=swap" rel="stylesheet">

<style>
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  :root {
    --cream: oklch(0.98 0.008 85);
    --linen: oklch(0.93 0.01 85);
    --espresso: oklch(0.22 0.015 65);
    --terracotta: oklch(0.32 0.08 255);
    --border: oklch(0.88 0.012 85);
    --white: oklch(1 0.004 85);
    --terracotta-light: oklch(0.32 0.08 255 / 0.08);
    --terracotta-mid: oklch(0.32 0.08 255 / 0.22);
    --muted: oklch(0.52 0.015 65);
    --body: oklch(0.52 0.015 65);
  }

  body {
    background: var(--cream);
    color: var(--espresso);
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    min-height: 100vh;
    padding: 40px 20px 60px;
  }

  .container {
    max-width: 680px;
    margin: 0 auto;
  }

  /* HEADER */
  .header {
    text-align: center;
    margin-bottom: 48px;
    padding-bottom: 36px;
    border-bottom: 1px solid var(--border);
  }

  .header .eyebrow {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--terracotta);
    margin-bottom: 14px;
  }

  .header h1 {
    font-family: 'Fraunces', Georgia, serif;
    font-size: clamp(28px, 5vw, 38px);
    font-weight: 700;
    color: var(--espresso);
    line-height: 1.2;
    margin-bottom: 14px;
  }

  .header p {
    font-size: 16px;
    color: var(--muted);
    line-height: 1.6;
    max-width: 480px;
    margin: 0 auto 20px;
  }

  .progress-wrap {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    margin-top: 20px;
  }

  .progress-bar {
    width: 200px;
    height: 4px;
    background: var(--border);
    border-radius: 2px;
    overflow: hidden;
  }

  .progress-fill {
    height: 100%;
    background: var(--terracotta);
    border-radius: 2px;
    transition: width 0.5s ease;
    width: 0%;
  }

  .progress-text {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 12px;
    color: oklch(0.52 0.015 65);
  }

  /* STEPS */
  .step {
    background: var(--white);
    border: 1px solid var(--border);
    border-radius: 3px;
    margin-bottom: 16px;
    overflow: hidden;
    transition: all 0.3s ease;
  }

  .step.completed {
    border-color: var(--terracotta-mid);
    background: var(--terracotta-light);
  }

  .step-header {
    display: flex;
    align-items: flex-start;
    gap: 18px;
    padding: 24px 28px;
    cursor: pointer;
    user-select: none;
  }

  .step-check {
    width: 28px;
    height: 28px;
    border: 2px solid var(--border);
    border-radius: 50%;
    flex-shrink: 0;
    margin-top: 2px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
    background: var(--white);
  }

  .step.completed .step-check {
    background: var(--terracotta);
    border-color: var(--terracotta);
  }

  .step-check svg {
    opacity: 0;
    transform: scale(0.5);
    transition: all 0.25s ease;
  }

  .step.completed .step-check svg {
    opacity: 1;
    transform: scale(1);
  }

  .step-title-wrap { flex: 1; }

  .step-number {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    color: var(--terracotta);
    margin-bottom: 4px;
  }

  .step.completed .step-number { color: oklch(0.26 0.075 255); }

  .step-title {
    font-family: 'Fraunces', Georgia, serif;
    font-size: 20px;
    font-weight: 600;
    color: var(--espresso);
    line-height: 1.3;
  }

  .step-toggle {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 20px;
    color: var(--border);
    flex-shrink: 0;
    line-height: 1;
    margin-top: 4px;
    transition: transform 0.3s ease, color 0.3s ease;
  }

  .step.open .step-toggle { transform: rotate(45deg); color: var(--terracotta); }

  /* STEP BODY */
  .step-body {
    display: none;
    padding: 0 28px 28px 74px;
  }

  .step.open .step-body { display: block; }

  .step-body p {
    font-size: 16px;
    line-height: 1.7;
    color: var(--body);
    margin-bottom: 14px;
  }

  .step-body ul {
    list-style: none;
    margin-bottom: 16px;
  }

  .step-body ul li {
    font-size: 16px;
    line-height: 1.6;
    color: var(--body);
    padding: 5px 0 5px 20px;
    position: relative;
  }

  .step-body ul li::before {
    content: "—";
    position: absolute;
    left: 0;
    color: var(--terracotta);
    font-weight: 300;
  }

  /* PRINCIPLE BOX */
  .principle-box {
    background: var(--linen);
    border-left: 3px solid var(--terracotta);
    border-radius: 0 3px 3px 0;
    padding: 14px 18px;
    margin: 12px 0 16px;
  }

  .principle-box .label {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--terracotta);
    margin-bottom: 6px;
  }

  .principle-box p {
    font-size: 14px;
    color: var(--espresso);
    line-height: 1.6;
    margin: 0;
    font-style: italic;
  }

  /* PROMPT BLOCK */
  .prompt-block {
    background: oklch(0.98 0.008 85);
    border: 1px solid var(--border);
    border-left: 3px solid var(--espresso);
    border-radius: 0 3px 3px 0;
    padding: 14px 18px;
    margin: 12px 0 16px;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 14px;
    color: var(--espresso);
    line-height: 1.6;
  }

  /* MARK DONE */
  .mark-done-btn {
    display: inline-block;
    margin-top: 8px;
    padding: 10px 22px;
    background: var(--terracotta);
    color: var(--white);
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 14px;
    font-weight: 600;
    letter-spacing: 0.03em;
    border: none;
    border-radius: 2px;
    cursor: pointer;
    transition: background 0.2s ease;
  }

  .mark-done-btn:hover { background: oklch(0.26 0.075 255); }
  .step.completed .mark-done-btn { background: oklch(0.26 0.075 255); }

  /* TIP BOX */
  .tip-box {
    background: var(--linen);
    border: 1px solid var(--border);
    border-radius: 3px;
    padding: 24px 28px;
    margin-top: 8px;
    display: flex;
    gap: 16px;
    align-items: flex-start;
  }

  .tip-icon {
    width: 36px;
    height: 36px;
    flex-shrink: 0;
    margin-top: 1px;
    color: var(--terracotta);
  }

  .tip-box p {
    font-size: 16px;
    line-height: 1.7;
    color: var(--body);
    margin: 0;
  }

  /* COMPLETION */
  .completion {
    display: none;
    text-align: center;
    padding: 40px 28px;
    background: var(--white);
    border: 1px solid var(--terracotta-mid);
    border-radius: 3px;
    margin-top: 8px;
  }

  .completion.visible { display: block; }

  .completion .icon {
    width: 48px;
    height: 48px;
    margin: 0 auto 16px;
    color: var(--terracotta);
  }

  .completion h2 {
    font-family: 'Fraunces', Georgia, serif;
    font-size: 24px;
    color: var(--espresso);
    margin-bottom: 10px;
  }

  .completion p {
    font-size: 16px;
    color: var(--muted);
    line-height: 1.6;
    max-width: 420px;
    margin: 0 auto 20px;
  }

  .completion a {
    display: inline-block;
    padding: 12px 28px;
    background: var(--terracotta);
    color: var(--white);
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 14px;
    font-weight: 600;
    border-radius: 2px;
    text-decoration: none;
    transition: background 0.2s;
  }

  .completion a:hover { background: oklch(0.26 0.075 255); }

  /* FOOTER */
  .footer {
    text-align: center;
    margin-top: 48px;
    padding-top: 28px;
    border-top: 1px solid var(--border);
  }

  .footer p {
    font-family: 'Plus Jakarta Sans', system-ui, sans-serif;
    font-size: 12px;
    color: oklch(0.88 0.012 85);
    letter-spacing: 0.04em;
  }

  .footer a { color: var(--terracotta); text-decoration: none; }
</style>
</head>
<body>
<div class="container">

  <!-- HEADER -->
  <div class="header">
    <p class="eyebrow">Resource Center · Smart Stack</p>
    <h1>8 Ways to Think<br>With Claude</h1>
    <p>Because the goal was never more output, it was clearer thinking.</p>
    <div class="progress-wrap">
      <div class="progress-bar"><div class="progress-fill" id="progressFill"></div></div>
      <span class="progress-text" id="progressText">0 of 8 complete</span>
    </div>
  </div>

  <!-- WAY 1 -->
  <div class="step open" id="step1">
    <div class="step-header" onclick="toggleStep('step1')">
      <div class="step-check">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M2.5 7L5.5 10L11.5 4" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div class="step-title-wrap">
        <div class="step-number">Way 01</div>
        <div class="step-title">Know Yourself Before You Ask Anything</div>
      </div>
      <div class="step-toggle">+</div>
    </div>
    <div class="step-body">
      <p>Claude meets you fresh every session. Before you type a single word, it has no idea who you are, what you're building, or what matters to you right now. The first way to think with Claude is to decide what it needs to know about you, and write it down once.</p>
      <p>Your role, your audience, your current focus, the decisions already made. That briefing becomes the foundation everything else builds on.</p>
      <div class="principle-box">
        <div class="label">The Shift</div>
        <p>Stop expecting Claude to figure you out. Give it the briefing a great assistant would actually need.</p>
      </div>
      <button class="mark-done-btn" onclick="markDone('step1')">✓ Got it</button>
    </div>
  </div>

  <!-- WAY 2 -->
  <div class="step" id="step2">
    <div class="step-header" onclick="toggleStep('step2')">
      <div class="step-check">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M2.5 7L5.5 10L11.5 4" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div class="step-title-wrap">
        <div class="step-number">Way 02</div>
        <div class="step-title">Know How You Sound</div>
      </div>
      <div class="step-toggle">+</div>
    </div>
    <div class="step-body">
      <p>If Claude writes for you and it doesn't sound like you, the problem isn't Claude: it's that you haven't told it what "you" actually sounds like. This is about knowing your own voice well enough to describe it.</p>
      <p>Your rhythm. Your warmth. The words that feel right and the ones that make you cringe. When you can articulate that, Claude can match it.</p>
      <div class="principle-box">
        <div class="label">The Shift</div>
        <p>You can't outsource your voice until you own it. Describe it first, then Claude can hold it for you.</p>
      </div>
      <button class="mark-done-btn" onclick="markDone('step2')">✓ Got it</button>
    </div>
  </div>

  <!-- WAY 3 -->
  <div class="step" id="step3">
    <div class="step-header" onclick="toggleStep('step3')">
      <div class="step-check">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M2.5 7L5.5 10L11.5 4" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div class="step-title-wrap">
        <div class="step-number">Way 03</div>
        <div class="step-title">Know What You'd Never Say</div>
      </div>
      <div class="step-toggle">+</div>
    </div>
    <div class="step-body">
      <p>Taste isn't just what you love: it's what you reject. Most people tell Claude what they want. The sharper move is telling it what you'd never write.</p>
      <p>The corporate phrases, the hollow structures, the tone that makes your audience skim past. Your no-list trains Claude faster than your yes-list ever will.</p>
      <div class="principle-box">
        <div class="label">The Shift</div>
        <p>Your editorial "no" is more powerful than your "yes." Build the list and share it every time.</p>
      </div>
      <button class="mark-done-btn" onclick="markDone('step3')">✓ Got it</button>
    </div>
  </div>

  <!-- WAY 4 -->
  <div class="step" id="step4">
    <div class="step-header" onclick="toggleStep('step4')">
      <div class="step-check">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M2.5 7L5.5 10L11.5 4" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div class="step-title-wrap">
        <div class="step-number">Way 04</div>
        <div class="step-title">Ask Claude to Ask You Questions First</div>
      </div>
      <div class="step-toggle">+</div>
    </div>
    <div class="step-body">
      <p>Most people give Claude instructions and hope for the best. Flip it. Ask Claude to surface questions before it starts: about your goal, your audience, what success looks like.</p>
      <div class="prompt-block">I want to [TASK] so that [SUCCESS CRITERIA]. Ask me questions before you begin. Show me your plan. Wait for my approval.</div>
      <p>That two-minute exchange changes everything. You stop receiving output and start having a conversation. That's the shift from using Claude to thinking with it.</p>
      <div class="principle-box">
        <div class="label">The Shift</div>
        <p>The best prompt isn't the one that gets the fastest answer. It's the one that starts the right conversation.</p>
      </div>
      <button class="mark-done-btn" onclick="markDone('step4')">✓ Got it</button>
    </div>
  </div>

  <!-- WAY 5 -->
  <div class="step" id="step5">
    <div class="step-header" onclick="toggleStep('step5')">
      <div class="step-check">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M2.5 7L5.5 10L11.5 4" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div class="step-title-wrap">
        <div class="step-number">Way 05</div>
        <div class="step-title">Start With the Outcome, Not the Task</div>
      </div>
      <div class="step-toggle">+</div>
    </div>
    <div class="step-body">
      <p>Claude can write you an email. But if you tell it why that email exists (what you want the reader to feel, what you want them to do, what relationship you're in with them), it writes a completely different email.</p>
      <p>Before you describe the task, describe done. What does the finished thing actually accomplish?</p>
      <div class="principle-box">
        <div class="label">The Shift</div>
        <p>Don't describe the deliverable. Describe the result the deliverable needs to create.</p>
      </div>
      <button class="mark-done-btn" onclick="markDone('step5')">✓ Got it</button>
    </div>
  </div>

  <!-- WAY 6 -->
  <div class="step" id="step6">
    <div class="step-header" onclick="toggleStep('step6')">
      <div class="step-check">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M2.5 7L5.5 10L11.5 4" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div class="step-title-wrap">
        <div class="step-number">Way 06</div>
        <div class="step-title">Give Claude a Role, Not Just a Request</div>
      </div>
      <div class="step-toggle">+</div>
    </div>
    <div class="step-body">
      <p>"Fix this" and "read this as my ideal client and tell me what's missing" are not the same instruction. When you give Claude a specific role (editor, strategist, skeptic, your reader), it stops guessing and starts thinking from a particular point of view.</p>
      <p>The role shapes everything it notices, questions, and offers. You get a thinking partner, not a text machine.</p>
      <div class="principle-box">
        <div class="label">The Shift</div>
        <p>A request gets you output. A role gets you perspective. Assign the role before you assign the task.</p>
      </div>
      <button class="mark-done-btn" onclick="markDone('step6')">✓ Got it</button>
    </div>
  </div>

  <!-- WAY 7 -->
  <div class="step" id="step7">
    <div class="step-header" onclick="toggleStep('step7')">
      <div class="step-check">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M2.5 7L5.5 10L11.5 4" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div class="step-title-wrap">
        <div class="step-number">Way 07</div>
        <div class="step-title">Refine, Don't Regenerate</div>
      </div>
      <div class="step-toggle">+</div>
    </div>
    <div class="step-body">
      <p>When Claude's output isn't right, the instinct is to start over. The better move is to stay in the conversation and tell it exactly what's off. Too formal. Too long. That second paragraph lost my voice.</p>
      <p>Every refinement teaches Claude something about your standard. Regenerating from scratch resets that learning every time. Think of it as sculpting, not vending.</p>
      <div class="principle-box">
        <div class="label">The Shift</div>
        <p>Stay in the conversation. Each correction makes the next draft closer. Starting over erases that progress.</p>
      </div>
      <button class="mark-done-btn" onclick="markDone('step7')">✓ Got it</button>
    </div>
  </div>

  <!-- WAY 8 -->
  <div class="step" id="step8">
    <div class="step-header" onclick="toggleStep('step8')">
      <div class="step-check">
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
          <path d="M2.5 7L5.5 10L11.5 4" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
      </div>
      <div class="step-title-wrap">
        <div class="step-number">Way 08</div>
        <div class="step-title">One Input, Many Outputs</div>
      </div>
      <div class="step-toggle">+</div>
    </div>
    <div class="step-body">
      <p>A single conversation with Claude can produce more than one thing. A recording becomes a summary, a social post, a newsletter opening, a short clip description. A rough idea becomes a framework, an outline, and a first draft.</p>
      <p>Before you close a Claude conversation, ask: what else could come from this? That question changes how much you get from every session.</p>
      <div class="principle-box">
        <div class="label">The Shift</div>
        <p>Don't close the conversation when you have the thing you came for. Ask what else it can become.</p>
      </div>
      <button class="mark-done-btn" onclick="markDone('step8')">✓ Got it</button>
    </div>
  </div>

  <!-- TIP -->
  <div class="tip-box">
    <svg class="tip-icon" viewBox="0 0 36 36" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
      <circle cx="18" cy="18" r="15"/>
      <path d="M18 12v6l4 2"/>
    </svg>
    <p><strong>The short version:</strong> You don't need more tools. You need a better thinking partner. Claude is that, but only when you show up knowing who you are, what you sound like, and what you actually want to accomplish. The eight ways above are how you do that.</p>
  </div>

  <!-- COMPLETION -->
  <div class="completion" id="completion">
    <svg class="icon" viewBox="0 0 48 48" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
      <circle cx="24" cy="24" r="20"/>
      <path d="M14 24l7 7 13-13"/>
    </svg>
    <h2>You've got the full picture.</h2>
    <p>Now put it to work. Open Claude, paste your briefing, and start thinking, not just prompting.</p>
    <a href="https://aitools.yoursmartstack.com/" target="_blank">Explore the Resource Center →</a>
  </div>

  <!-- FOOTER -->

</div>

<script>
  const steps = ['step1','step2','step3','step4','step5','step6','step7','step8'];

  function toggleStep(id) {
    const el = document.getElementById(id);
    const isOpen = el.classList.contains('open');
    steps.forEach(s => document.getElementById(s).classList.remove('open'));
    if (!isOpen) el.classList.add('open');
  }

  function markDone(id) {
    const el = document.getElementById(id);
    el.classList.add('completed');
    el.classList.remove('open');
    updateProgress();
  }

  function updateProgress() {
    const done = steps.filter(s => document.getElementById(s).classList.contains('completed')).length;
    const pct = (done / steps.length) * 100;
    document.getElementById('progressFill').style.width = pct + '%';
    document.getElementById('progressText').textContent = done + ' of ' + steps.length + ' complete';
    if (done === steps.length) {
      document.getElementById('completion').classList.add('visible');
    }
  }

  /* No toggleStep('step1') call here, deliberately. Step 1 already carries the
     open class in the markup, so calling toggle CLOSES it — every one of these
     guides loaded with all its cards shut and nothing to show that a card opens
     at all. The markup is the intent. Fixed 4 Sep 2026. */
</script>
</body>
</html>
`;

export default function Claude8Wins() {
  return (
    <GuideShell
      context={"Claude guide"}
      backHref={"/resources/claude"}
      backLabel={"Back to Claude"}
    >
      <GuideFrame html={HTML_SRC} title={"8 Ways to Think With Claude"} />
    </GuideShell>
  );
}
